import { AntDesign } from "@expo/vector-icons";
import { of } from "@nozbe/watermelondb/utils/rx";
import {
  withObservables,
  ExtractedObservables,
} from "@nozbe/watermelondb/react";
import { useNavigation } from "@react-navigation/native";
import { SaleAdjustmentType } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useCallback } from "react";
import { Colors } from "react-native-ui-lib";
import { compose } from "recompose";
import Sale from "../../../../../../utilities/data/model/Sale";
import { PageItemType } from "../../../../../../utilities/layout";
import { moneyToFormattedString } from "../../../../../../utilities/money";
import { SellScreenProps } from "../../../../screens/SellScreen";
import { withSaleController } from "../../../../utils/sale-controller";
import { QuickKey as BaseQuickKey, QuickKeyProps } from "../../../QuickKey";

type DiscountSaleQuickKeyRendererProps = ExtractedObservables<
  ReturnType<typeof getObserables>
> & {
  sale?: Sale;
  item: PageItemType;
  innerRef?: React.MutableRefObject<any>;
} & QuickKeyProps;

const getObserables = ({ sale }: { sale: Sale }) => ({
  items: sale ? sale.items.observeWithColumns(["adjustments"]) : of(null),
});
export const DiscountSaleQuickKeyRenderer = React.forwardRef<
  any,
  DiscountSaleQuickKeyRendererProps
>(({ sale, items, item, innerRef, disabled: _disabled, ...rest }, ref) => {
  const [canModifySale, setCanModifySale] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      if (sale) {
        const r = await sale.canModifySale();
        setCanModifySale(r);
      }
    })();
  }, [sale]);

  const navigation = useNavigation<SellScreenProps["navigation"]>();

  const hasTradeDiscount = items?.some((item) =>
    item.adjustments.find(
      (i) => i.type === SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT
    )
  );

  const disabled =
    _disabled !== undefined
      ? _disabled
      : canModifySale == false ||
        !sale ||
        !items ||
        items.length === 0 ||
        hasTradeDiscount;

  const isRemoveDiscount = items?.some((item) =>
    item.adjustments.find((i) => i.type === SaleAdjustmentType.SALE_DISCOUNT)
  );

  const title = isRemoveDiscount ? "Remove Sale Discount" : "Sale Discount";

  const adjustmentTotal = items?.reduce((acc, item) => {
    const adjustment = item.adjustments.find(
        (i) => i.type === SaleAdjustmentType.SALE_DISCOUNT
      ),
      adjustmentTotal = adjustment ? adjustment.amount * item.quantity : 0;

    return acc + adjustmentTotal;
  }, 0);

  const handleOnPress = useCallback(() => {
    if (disabled) return;
    if (isRemoveDiscount) {
      return sale?.removeSaleDiscount().catch((e) => console.log(e));
    }
    navigation.navigate("ActionsRootScreen", {
      screen: "DiscountSaleScreen",
    });
  }, [sale, isRemoveDiscount, disabled, navigation]);

  return (
    <BaseQuickKey
      ref={ref}
      disabled={disabled}
      isDestructive={isRemoveDiscount}
      backgroundColor={
        isRemoveDiscount
          ? Colors.quickKeyDestructionText
          : Colors.quickKeyLightgreenBackground
      }
      isModifier={!isRemoveDiscount}
      iconPrimary={
        <AntDesign
          name="tagso"
          size={24}
          color={
            disabled
              ? Colors.white
              : isRemoveDiscount
              ? Colors.quickKeyDestructionText
              : "#7dd49f"
          }
        />
      }
      onPress={handleOnPress}
      label={title}
      subtitle={
        isRemoveDiscount ? moneyToFormattedString(adjustmentTotal) : undefined
      }
      {...rest}
    />
  );
});

export const DiscountSaleQuickKey = compose(
  withSaleController,
  withObservables(["sale"], getObserables)
)(DiscountSaleQuickKeyRenderer as any);
